import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, MapPin, Phone, MessageCircle, Heart, Loader2, Sun, Camera, Coffee } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SEO from '../components/SEO';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { AnimatedSection, AnimatedCard } from '../components/AnimatedComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DayToursPage = () => {
  const { settings } = useSiteSettings();
  const contactInfo = settings?.contact_info || {};
  const [dayTours, setDayTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTour, setSelectedTour] = useState(null);
  const [showEnquiryDialog, setShowEnquiryDialog] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [likedTours, setLikedTours] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', email: '', phone: '', persons: '2', date: '', message: '' });

  useEffect(() => {
    const fetchDayTours = async () => {
      try {
        const res = await fetch(`${API_URL}/api/packages`);
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        // Filter only 1-day tours (duration_days === 1 or duration_nights === 0 or 1)
        const oneDayTours = data.filter(pkg => 
          pkg.duration_days === 1 || 
          pkg.duration_nights === 0 || 
          pkg.duration_nights === 1
        );
        setDayTours(oneDayTours);
      } catch (e) {
        console.error('Error fetching day tours:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchDayTours();
  }, []);

  const toggleLike = (id) => setLikedTours(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  
  const handleEnquiry = (tour) => { 
    setSelectedTour(tour); 
    setShowEnquiryDialog(true); 
  };
  
  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: enquiryForm.name,
          email: enquiryForm.email,
          phone: enquiryForm.phone,
          message: `DAY TOUR ENQUIRY\n${'─'.repeat(30)}\nTour: ${selectedTour?.title}\nDestination: ${selectedTour?.destination}\nPersons: ${enquiryForm.persons}\nPreferred Date: ${enquiryForm.date}\nNotes: ${enquiryForm.message}`,
          enquiry_type: 'day_tour',
          package_id: selectedTour?.id
        })
      });
      if (!res.ok) throw new Error('Failed to submit');
      alert(`Enquiry submitted for ${selectedTour?.title}!`);
      setShowEnquiryDialog(false);
      setEnquiryForm({ name: '', email: '', phone: '', persons: '2', date: '', message: '' });
    } catch (err) {
      alert('Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsAppEnquiry = (tour) => {
    window.open(`https://wa.me/${(contactInfo.whatsapp || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi! I'm interested in the day tour: ${tour.title} (${tour.destination}).`)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-s-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-s-primary" />
      </div>
    );
  }

  return (
    <div className="bg-s-bg min-h-screen">
      <SEO 
        title="Day Tours - One Day Trips | Sentora Holidays" 
        description="Explore our curated collection of day tours and one-day trips. Perfect for quick getaways and sightseeing adventures across India."
        keywords="day tours, one day trips, sightseeing, day excursions, short trips India"
      />

      {/* Hero */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1920&q=80)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-s-bg via-s-bg/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-s-bg via-transparent to-s-bg/50" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <Badge className="bg-s-primary text-white border-0 mb-3 md:mb-4 text-xs badge-pop"><Sun size={12} className="mr-1" /> DAY TOURS</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-s-text mb-3 md:mb-4 font-heritage italic hero-text-reveal">Day Tours</h1>
            <p className="text-sm md:text-lg lg:text-xl text-s-text2 mb-4 md:mb-6 hero-text-reveal-delay">Perfect one-day experiences for quick getaways</p>
            <div className="flex gap-4 md:gap-8 hero-text-reveal-delay">
              {[{ icon: Sun, label: 'Day Trips', value: `${dayTours.length}+` }, { icon: Camera, label: 'Experiences', value: 'Unique' }, { icon: Coffee, label: 'Duration', value: '1 Day' }].map((stat, idx) => (
                <div key={idx} className="flex items-center gap-1.5 md:gap-2">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-white/10 rounded-lg flex items-center justify-center"><stat.icon className="w-4 h-4 md:w-5 md:h-5 text-s-primary" /></div>
                  <div><p className="text-s-text font-bold text-sm md:text-base">{stat.value}</p><p className="text-s-text2 text-[10px] md:text-sm">{stat.label}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tours List */}
      {dayTours.length === 0 ? (
        <section className="max-w-7xl mx-auto px-4 py-20 text-center">
          <Sun className="w-16 h-16 text-s-muted mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-s-text mb-2">No Day Tours Available</h2>
          <p className="text-s-muted mb-6">We're working on adding exciting day tours. Check back soon!</p>
          <Link to="/packages">
            <Button className="bg-s-primary text-white">View All Packages</Button>
          </Link>
        </section>
      ) : (
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {dayTours.map((tour, idx) => (
              <AnimatedCard key={tour.id} delay={idx * 80} hoverEffect="none" className="group" onMouseEnter={() => setHoveredCard(tour.id)} onMouseLeave={() => setHoveredCard(null)}>
                <div className={`relative rounded-2xl overflow-hidden bg-s-bg2 transition-all duration-500 ${hoveredCard === tour.id ? 'scale-105 shadow-2xl shadow-s-primary/20 z-10' : ''}`}>
                  {/* Image Section */}
                  <div className="relative h-56 overflow-hidden">
                    <img loading="lazy" src={tour.image} alt={tour.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* Like Button */}
                    <button onClick={() => toggleLike(tour.id)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors">
                      <Heart className={`w-5 h-5 ${likedTours.includes(tour.id) ? 'fill-red-500 text-s-primary' : 'text-gray-600'}`} />
                    </button>
                    
                    {/* Duration Badge */}
                    <Badge className="absolute top-4 left-4 bg-s-primary text-white">
                      <Clock size={12} className="mr-1" /> 1 Day
                    </Badge>
                    
                    {/* Tour Info */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-1">{tour.title}</h3>
                      <p className="text-white/80 text-sm flex items-center gap-1">
                        <MapPin size={14} /> {tour.destination}
                      </p>
                    </div>
                  </div>
                  
                  {/* Details Section */}
                  <div className="p-5">
                    <p className="text-s-text2 text-sm mb-4 line-clamp-2">{tour.brief || 'Experience an unforgettable day tour filled with local culture, sightseeing, and memorable moments.'}</p>
                    
                    {/* Highlights */}
                    {tour.highlights?.slice(0, 3).length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {tour.highlights.slice(0, 3).map((h, i) => (
                          <span key={i} className="text-xs bg-s-bg px-2 py-1 rounded-full text-s-text2">{h}</span>
                        ))}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button onClick={() => handleEnquiry(tour)} className="flex-1 bg-s-primary hover:bg-s-primary-h text-white rounded-xl">
                        <Phone size={16} className="mr-2" /> Enquire
                      </Button>
                      <Button onClick={() => handleWhatsAppEnquiry(tour)} variant="outline" className="border-green-500 text-s-primary hover:bg-s-primary/10 rounded-xl">
                        <MessageCircle size={16} />
                      </Button>
                      <Link to={`/packages/${tour.id}`}>
                        <Button variant="outline" className="border-s-border text-s-text hover:bg-s-bg2 rounded-xl">
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </section>
      )}

      {/* Enquiry Dialog */}
      <Dialog open={showEnquiryDialog} onOpenChange={setShowEnquiryDialog}>
        <DialogContent className="bg-s-card border-s-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-s-text">Enquire About {selectedTour?.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEnquirySubmit} className="space-y-4">
            <div>
              <Label className="text-s-text">Full Name *</Label>
              <Input value={enquiryForm.name} onChange={(e) => setEnquiryForm({...enquiryForm, name: e.target.value})} required className="bg-s-bg border-s-border text-s-text" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-s-text">Email *</Label>
                <Input type="email" value={enquiryForm.email} onChange={(e) => setEnquiryForm({...enquiryForm, email: e.target.value})} required className="bg-s-bg border-s-border text-s-text" />
              </div>
              <div>
                <Label className="text-s-text">Phone *</Label>
                <Input value={enquiryForm.phone} onChange={(e) => setEnquiryForm({...enquiryForm, phone: e.target.value})} required className="bg-s-bg border-s-border text-s-text" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-s-text">No. of Persons</Label>
                <select value={enquiryForm.persons} onChange={(e) => setEnquiryForm({...enquiryForm, persons: e.target.value})} className="w-full h-10 px-3 rounded-md bg-s-bg border border-s-border text-s-text">
                  {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-s-text">Preferred Date</Label>
                <Input type="date" value={enquiryForm.date} onChange={(e) => setEnquiryForm({...enquiryForm, date: e.target.value})} className="bg-s-bg border-s-border text-s-text" />
              </div>
            </div>
            <div>
              <Label className="text-s-text">Additional Notes</Label>
              <Textarea value={enquiryForm.message} onChange={(e) => setEnquiryForm({...enquiryForm, message: e.target.value})} className="bg-s-bg border-s-border text-s-text" placeholder="Any special requirements..." />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-s-primary text-white">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : 'Submit Enquiry'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DayToursPage;
